import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend, ChartOptions, ChartData } from 'chart.js';
import { Tramo } from '@componentes/Tabla/types';
import { obtenerEstado, obtenerEstadoTrafico } from '@utils/estadoTrafico';
import { red, blue, green, pink, yellow } from '@mui/material/colors';
import { Link } from 'react-router-dom';
import { useWindowSize } from '@utils/resize';

Chart.register(ArcElement, Tooltip, Legend);

interface EstadoTraficoPrincipalProps {
    info: Tramo[];
}

const EstadoTraficoPrincipal: React.FC<EstadoTraficoPrincipalProps> = ({ info }) => {
    const rootStyles = getComputedStyle(document.documentElement);
    const { width } = useWindowSize();
    const primaryColor = rootStyles.getPropertyValue('--primary-color');
    const thirdColor = rootStyles.getPropertyValue('--fourth-color');
    const secondaryColor = rootStyles.getPropertyValue('--secondary-color');
    const [nivelTrafico, setNivelTrafico] = useState(3)
    const [estado, setEstado] = useState(info.map(ele => obtenerEstadoTrafico(ele.CARGA, 3)));
    const [titulos, setTitulos] = useState([''])
    const [trafico, setTrafico] = useState<{ nombre: string, color: string }[][]>([]);
    const [colores, setColores] = useState([''])

    useEffect(() => {
        // Cargar el config.json desde la carpeta public
        const loadConfig = async () => {
            try {
                const response = await fetch('/config.json');
                const config = await response.json();

                config.NIVEL_TRAFICO === 3
                    ? setTitulos(["Tráfico Fluido", "Tráfico Intenso", "Retenciones"])
                    : setTitulos(["Muy Fluido", "Fluido", "Denso", "Muy Denso", "Congestión"]);

                config.NIVEL_TRAFICO === 3 ? setTrafico([estado.filter(ele => ele.nombre === 'Tráfico fluido'), estado.filter(ele => ele.nombre === 'Tráfico intenso'), estado.filter(ele => ele.nombre === 'Retenciones')])
                    : setTrafico([estado.filter(ele => ele.nombre === 'Congestión'), estado.filter(ele => ele.nombre === 'Muy Denso'), estado.filter(ele => ele.nombre === 'Denso'), estado.filter(ele => ele.nombre === 'Fluido'), estado.filter(ele => ele.nombre === 'Muy Fluido')]);

                config.NIVEL_TRAFICO === 3
                    ? setColores([green[400], yellow[800], red[500]])
                    : setColores([blue[500], green[500], yellow[500], pink[500], red[500]]);

                setEstado(info.map(ele => obtenerEstadoTrafico(ele.CARGA, config.NIVEL_TRAFICO)))
                setNivelTrafico(config.NIVEL_TRAFICO)
            } catch (error) {
                console.error('Error loading config:', error);
            }
        };

        loadConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dataValues = trafico.map(row => row.length);
    const isAllZero = dataValues.every(value => value === 0);
    const finalData = isAllZero ? [1, 0, 0] : dataValues;
    const data: ChartData<'doughnut'> = {
        labels: titulos,
        datasets: [{
            data: finalData,
            backgroundColor: colores,
            hoverBackgroundColor: colores,
            borderWidth: 1,
        }]
    };
    const options: ChartOptions<'doughnut'> = {
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        return `${titulos[tooltipItem.dataIndex]}: ${trafico[tooltipItem.dataIndex].length}`;
                    }
                }
            }
        },
        cutout: '80%',
        rotation: -135,
        circumference: 270,
        animation: {
            duration: 0
        }
    };

    const [estadoActual, setEstadoActual] = useState<string>('');

    useEffect(() => {
        const fetchEstado = async () => {
            const result = await obtenerEstado(trafico, estado.length, nivelTrafico);
            setEstadoActual(result);
        };

        fetchEstado();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trafico]);

    return (
        <Box sx={{ bgcolor: secondaryColor, borderRadius: 1, padding: '20px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: '90%', marginBottom: '10px' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: primaryColor }} >
                    <Link className={width > 1300 ? 'link-subruta' : 'not-link-subruta'} to={width > 1300 ? '/estado' : ''} style={{ width: '20%' }}>
                        Estado del Tráfico
                    </Link>
                </Typography>
            </div>

            <Box sx={{ bgcolor: thirdColor, borderRadius: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '90%' }}>
                <Box sx={{ maxWidth: '150px', maxHeight: '150px', position: 'relative' }}>
                    <Doughnut data={data} options={options} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }} >
                    {estadoActual}
                </Typography>
            </Box>
        </Box>
    );
};

export default EstadoTraficoPrincipal;